import { useContext, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from 'redux/hooks';

import { BucketContext, type S3Item } from 'contexts/BucketContext';

import { DefaultLayout } from 'layouts/DefaultLayout';

import { Table } from 'components/Table';

import { useColumns } from './Technologies.data';
import * as S from './Technologies.styles';

type TechRow = {
  id: number | string;
  title: string;
  image?: string;
  file?: string;
};

export function Technologies(): React.ReactElement {
  const { getListItems, getUrl } = useContext(BucketContext);
  const { season, marketLine } = useAppSelector((state) => state.settings);
  const [rows, setRows] = useState<TechRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const columns = useColumns();

  const PREFIX = useMemo(
    () =>
      season && marketLine
        ? `catalogue/products/${season}/${marketLine.toLocaleLowerCase()}/tecnologie/`
        : '',
    [season, marketLine]
  );

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        if (!PREFIX) {
          setRows([]);
          return;
        }
        setIsLoading(true);
        const res = await getListItems(PREFIX);
        const contents: { Key: string }[] = res?.Contents || [];

        // Collect technology names from keys directly under PREFIX
        const names = new Set<string>();
        contents.forEach(({ Key }) => {
          if (!Key || !Key.startsWith(PREFIX)) return;
          const rest = Key.slice(PREFIX.length);
          const first = rest.split('/')[0];
          if (first) names.add(first);
        });

        const techNames = Array.from(names).sort((a, b) => a.localeCompare(b));

        const built: TechRow[] = await Promise.all(
          techNames.map(async (name, idx) => {
            const logoKey = contents.find(
              ({ Key }) =>
                Key && Key.startsWith(`${PREFIX}${name}/logo/`) && !Key.endsWith('/')
            )?.Key;
            const fileKey = contents.find(
              ({ Key }) =>
                Key && Key.startsWith(`${PREFIX}${name}/file/`) && !Key.endsWith('/')
            )?.Key;

            const [imageUrl, fileUrl] = await Promise.all([
              logoKey
                ? getUrl(logoKey)
                    .then((r: S3Item) => r.url)
                    .catch(() => undefined)
                : undefined,
              fileKey
                ? getUrl(fileKey)
                    .then((r: S3Item) => r.url)
                    .catch(() => undefined)
                : undefined,
            ]);

            return {
              id: idx,
              title: name,
              image: imageUrl,
              file: fileUrl,
            } as TechRow;
          })
        );

        if (isMounted) setRows(built);
      } catch (e) {
        if (isMounted) setRows([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [getListItems, getUrl, PREFIX]);

  // Ensure stable data reference for Table if needed
  const data = useMemo(() => rows, [rows]);

  return (
    <DefaultLayout isNested withHeader>
      <S.HeadlineInfo fontWeight="normal" scale="s" tag="h4">
        List
      </S.HeadlineInfo>

      <S.Wrapper>
        <Table className="tech-table" columns={columns} data={data} isLoading={isLoading} />
      </S.Wrapper>
    </DefaultLayout>
  );
}
